import { useCallback, useEffect, useState } from 'react'
import { EditorState, convertToRaw, ContentState } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import HTMLReactParser from 'html-react-parser'

// html-to-draftjsは先頭がトップレベルの<img>だと、エンティティを持たないatomicブロックを生成してしまう。
// そのブロックはWysiwygMediaで描画される際にdraft-jsが例外を投げるため、
// 先頭に空の段落を補って画像がatomicブロックに正しく紐づくようにする。
export const prependEmptyParagraphIfStartsWithImage = (html: string): string =>
  /^\s*<img[\s>]/i.test(html) ? `<p></p>${html}` : html

const useWysiwygEditor = () => {
  const [editorState, setEditorState] = useState<EditorState>()
  const [currentLength, setCurrentLength] = useState<number>(0)

  const parseEditorStateToHtml = useCallback((): string => {
    if (!editorState) return ''
    const html = draftToHtml(convertToRaw(editorState.getCurrentContent()))
    // 不要なspanタグを削除
    return html.replace(/(<span([^>]+)>)/gi, '').replace(/(<\/span>)/gi, '')
  }, [editorState])

  const parseEditorStateToJsx = useCallback((): JSX.Element | JSX.Element[] => {
    return HTMLReactParser(parseEditorStateToHtml())
  }, [parseEditorStateToHtml])

  const onEditorStateChange = useCallback(
    (nextState: EditorState): void => {
      setEditorState(nextState)
    },
    [setEditorState]
  )

  const initEditorState = useCallback(
    (initialValue: string): void => {
      const contentBlock = htmlToDraft(prependEmptyParagraphIfStartsWithImage(initialValue))
      if (!contentBlock) return
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
      setEditorState(EditorState.createWithContent(contentState))
    },
    [setEditorState]
  )

  const replaceImage = useCallback(
    (src: string, dist: string) => {
      setEditorState((prevState) => {
        if (!prevState) return prevState
        const html = draftToHtml(convertToRaw(prevState.getCurrentContent()))
        const nextHtml = html.replace(src, dist)
        const contentBlock = htmlToDraft(prependEmptyParagraphIfStartsWithImage(nextHtml))
        if (!contentBlock) return prevState
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
        return EditorState.createWithContent(contentState)
      })
    },
    [setEditorState]
  )

  useEffect(() => {
    if (!editorState) return
    setCurrentLength(editorState.getCurrentContent().getPlainText().length)
  }, [editorState])

  return {
    currentLength,
    editorState,
    initEditorState,
    onEditorStateChange,
    parseEditorStateToHtml,
    parseEditorStateToJsx,
    replaceImage,
  }
}

export default useWysiwygEditor
